import clsx from "clsx";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  forwardRef,
} from "react";
import { Link } from "@/components/ui/link/Link";

const TableContext = createContext<{
  bleed: boolean;
  dense: boolean;
  grid: boolean;
  striped: boolean;
}>({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
});

type TableItem = {
  resource_type: string;
  display_name: string;
  metadata: {
    snapshot_id: string;
    resource_id: string;
    snapshot_date: string;
    created_at: string;
    data_quality: {
      completeness: number;
      last_updated: string;
      missing_fields: string[];
    };
  };
  data: Record<string, any>;
  styling: Array<string | null>;
  default_stream_type: string;
};

type Column = {
  id: string;
  name: string;
  field_path?: string;
};

export function Table({
  bleed = false,
  dense = false,
  grid = false,
  striped = false,
  /** Allow multi-line cell content (uses table-auto + normal whitespace). Default tables stay single-line for density. */
  wrap = false,
  className,
  children,
  items,
  columns,
  ...props
}: {
  bleed?: boolean;
  dense?: boolean;
  grid?: boolean;
  striped?: boolean;
  wrap?: boolean;
  items?: TableItem[];
  columns?: Column[];
} & React.ComponentPropsWithoutRef<"div">) {
  return (
    <TableContext.Provider
      value={
        { bleed, dense, grid, striped } as React.ContextType<
          typeof TableContext
        >
      }
    >
      <div className="flow-root flex-grow  w-full">
        <div
          {...props}
          className={clsx(
            className,
            "-mx-(--gutter)",
            wrap ? "whitespace-normal" : "whitespace-nowrap",
          )}
        >
          <div
            className={clsx(
              "inline-block min-w-full align-middle",
              !bleed && "sm:px-(--gutter)"
            )}
          >
            <table
              className={clsx(
                "min-w-full text-left text-sm text-slate-950 dark:text-white",
                wrap ? "table-auto" : "table-fixed",
              )}
            >
              {children || (
                <>
                  <TableHead>
                    <TableRow>
                      {columns?.map((column) => (
                        <TableHeader key={column.id}>{column.name}</TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items?.map((item, index) => {
                      // Debug log for each row's styling
                      console.log(`Row ${index} styling:`, item.styling);

                      return (
                        <TableRow key={index} item={item}>
                          {columns?.map((column, colIndex) => {
                            // Debug log for each cell's style
                            const cellStyle = item.styling[colIndex];
                            console.log(
                              `Cell [${index},${colIndex}] style:`,
                              cellStyle
                            );

                            return (
                              <TableCell
                                key={`${index}-${column.id}`}
                                style={cellStyle}
                              >
                                {item.data[column.field_path || column.id]}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </>
              )}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

export const TableHead = forwardRef<
  HTMLTableSectionElement,
  React.ComponentPropsWithoutRef<"thead">
>(({ className, ...props }, ref) => {
  return (
    <thead
      ref={ref}
      {...props}
      className={clsx(className, "text-slate-500 dark:text-slate-400")}
    />
  );
});
TableHead.displayName = "TableHead";

export function TableBody(props: React.ComponentPropsWithoutRef<"tbody">) {
  return <tbody {...props} />;
}

const TableRowContext = createContext<{
  href?: string;
  target?: string;
  title?: string;
}>({
  href: undefined,
  target: undefined,
  title: undefined,
});

export function TableRow({
  href,
  target,
  title,
  className,
  item,
  ...props
}: {
  href?: string;
  target?: string;
  title?: string;
  item?: TableItem;
} & React.ComponentPropsWithoutRef<"tr">) {
  const { striped } = useContext(TableContext);

  return (
    <TableRowContext.Provider
      value={
        { href, target, title } as React.ContextType<typeof TableRowContext>
      }
    >
      <tr
        {...props}
        className={clsx(
          className,
          href &&
            "has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-indigo-400 dark:focus-within:bg-white/[2.5%]",
          striped && "even:bg-slate-50 dark:even:bg-white/[2.5%]",
          href && striped && "hover:bg-slate-200 dark:hover:bg-white/5",
          href && !striped && "hover:bg-slate-200 dark:hover:bg-white/[2.5%]"
        )}
      />
    </TableRowContext.Provider>
  );
}

export function TableHeader({
  className,
  children,
  variant = "default",
  ...props
}: React.ComponentPropsWithoutRef<"th"> & {
  /** Use for compact controls (e.g. checkbox); skips line-clamp box that distorts borders. */
  variant?: "default" | "control" | "nowrap";
}) {
  const { bleed, grid } = useContext(TableContext);

  // Extract text alignment from className
  const isRightAligned = className?.includes("text-right");
  const isCenterAligned = className?.includes("text-center");

  let textAlignClass = "text-left";
  let justifyClass = "justify-start";

  if (isRightAligned) {
    textAlignClass = "text-right";
    justifyClass = "justify-end";
  } else if (isCenterAligned) {
    textAlignClass = "text-center";
    justifyClass = "justify-center";
  }

  if (variant === "control") {
    return (
      <th
        {...props}
        className={clsx(
          className,
          "border-b border-slate-200 z-20 px-3 py-3 align-middle text-slate-950 dark:text-white dark:border-b-slate-700",
          grid && !bleed && " sm:last:pr-4",
        )}
      >
        <div className={clsx("flex items-center", justifyClass)}>{children}</div>
      </th>
    );
  }

  if (variant === "nowrap") {
    return (
      <th
        {...props}
        className={clsx(
          className,
          "border-b border-slate-200 z-20 whitespace-nowrap text-gray-400 dark:text-gray-400 font-mono px-3 py-3 font-medium uppercase tracking-widest text-xs dark:border-b-slate-700",
          grid && !bleed && " sm:last:pr-4",
        )}
      >
        <div className={clsx("flex items-center", justifyClass, textAlignClass)}>{children}</div>
      </th>
    );
  }

  return (
    <th
      {...props}
      className={clsx(
        className,
        "border-b border-slate-200 z-20 text-gray-400 dark:text-gray-400 font-mono px-3 py-3 font-medium uppercase tracking-widest text-xs dark:border-b-slate-700",
        grid &&
          // "border-l border-l-slate-950/5 first:border-l-0 dark:border-l-white/5",
          !bleed &&
          " sm:last:pr-4"
      )}
    >
      <div
        className={`whitespace-normal leading-tight ${textAlignClass} max-h-[3.6em] mt-2 flex items-center ${justifyClass}`}
        style={{
          lineHeight: "1.2",
        }}
      >
        <div
          className="w-full"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: "1.4",
          }}
        >
          {children}
        </div>
      </div>
    </th>
  );
}

export function TableCell({
  className,
  children,
  style,
  ...props
}: {
  style?: string | null;
} & Omit<React.ComponentPropsWithoutRef<"td">, "style">) {
  const { bleed, dense, grid, striped } = useContext(TableContext);
  const { href, target, title } = useContext(TableRowContext);
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

  const parsedStyle = style ? parseInlineStyles(style) : undefined;

  const contrastTextColor = getContrastColor(parsedStyle?.backgroundColor);
  const isSticky = className?.includes("sticky");
  // Don't add transparent left border to sticky cells to avoid 1px alignment issues
  const coreClass = isSticky ? "" : "border-l border-transparent ";
  // Only add default padding if p-0 is not present in className
  const noPadding = className?.includes("p-0");

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        coreClass,
        !noPadding && "px-3 py-2.5",
        "relative",
        !striped && "border-b border-slate-200 dark:border-b-slate-700",
        grid && "border-l   first:border-l-0 dark:border-l-white/5",
        !bleed && "",
        contrastTextColor
      )}
    >
      {parsedStyle && (
        <div
          style={parsedStyle}
          className={
            "absolute z-[0] " +
            (className?.includes("sticky")
              ? " -inset-px group-hover/row:!bg-[#212f43]"
              : " inset-1 rounded")
          }
          aria-hidden="true"
        />
      )}
      <div className="relative z-[1]">{children}</div>
      {href && (
        <Link
          data-row-link
          href={href}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-hidden"
        />
      )}
    </td>
  );
}

function parseInlineStyles(styleString: string): React.CSSProperties {
  if (!styleString) return {};

  const styles: React.CSSProperties = {};
  const trimmedStyle = styleString.replace(/\s+/g, "");

  trimmedStyle.split(";").forEach((style) => {
    if (!style) return;

    const [property, value] = style.split(":");
    if (!property || !value) return;

    const cssProperty = property.trim();
    const cssValue = value.trim();

    switch (cssProperty) {
      case "backgroundColor":
      case "background-color": {
        // Use a solid background color
        const color = cssValue;
        styles.backgroundColor = color;
        break;
      }
      case "color":
        styles.color = cssValue;
        break;
      default: {
        const reactProperty = cssProperty
          .split("-")
          .map((part, i) =>
            i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
          )
          .join("");
        (styles as any)[reactProperty] = cssValue;
      }
    }
  });

  return styles;
}

// Helper function to determine text color based on background luminance
function getContrastColor(hexColor: string | undefined): string {
  if (!hexColor) return "text-slate-950 dark:text-white"; // Default if no color

  // Remove '#' if present
  hexColor = hexColor.replace(/^#/, "");

  // Convert hex to RGB
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // Calculate luminance using the WCAG formula
  // https://www.w3.org/TR/WCAG20/#relativeluminancedef
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  // Use threshold to decide between black and white text
  // Threshold can be adjusted (0.5 is common)
  return luminance > 0.5 ? "text-slate-950" : "text-white";
}
